if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.hideErrors = false;
      this._onSubmit = this.onSubmitHandler.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.querySelector('[name=id]')?.removeAttribute('disabled');
      this.form.addEventListener('submit', this._onSubmit);

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');

      if (document.querySelector('cart-drawer') && this.submitButton) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      const productInfo = this.closest('product-info');
      const sectionId = productInfo?.dataset?.section;

      if (sectionId) {
        this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== sectionId) return;
          this._syncFromVariantChange(event.data);
        });
      }

      this._initRestrictedAccessUI();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._onSubmit);
      this._variantChangeUnsubscriber?.();
    }

    _syncFromVariantChange({ html, variant, variantId }) {
      // `.product-form__buttons` is replaced on variant change, detaching the cached button.
      this.submitButton = this.querySelector('[type="submit"]') || this.submitButton;

      const resolvedVariantId = variantId || variant?.id;
      const variantInput = this.form?.querySelector('[name="id"]');

      if (variantInput && resolvedVariantId) {
        variantInput.value = resolvedVariantId;
        variantInput.disabled = false;
      }

      const sectionId = this.closest('product-info')?.dataset?.section;
      const addButtonUpdated = sectionId
        ? html.getElementById(`ProductSubmitButton-${sectionId}`)
        : null;
      const addButton = this.querySelector('[name="add"]');
      const addButtonText = addButton?.querySelector('span');

      if (addButton) {
        if (addButtonUpdated?.hasAttribute('disabled')) {
          addButton.setAttribute('disabled', 'disabled');
          if (addButtonText && window.variantStrings?.soldOut) {
            addButtonText.textContent = window.variantStrings.soldOut;
          }
        } else {
          addButton.removeAttribute('disabled');
        }
      }

      this.classList.toggle('product__not-available', !resolvedVariantId);
    }

    _initRestrictedAccessUI() {
      const restrictedMetaField = this.dataset.restricted;
      const restricted = getCookie('restricted');
      const addToCartButton = this.querySelector('.product-form__submit-button');
      const notifyMeButton = this.querySelector('.product__notify-button');

      if (this.closest('.product-card-wrapper')) {
        if (restrictedMetaField === 'true') {
          if (restricted !== 'disabled') {
            this.querySelector('.quick-add__submit')?.remove();

            const parentWrapper = this.closest('.product-card-wrapper');
            parentWrapper?.querySelector('.badge--sold-out')?.classList.remove('hidden');
          }
        }
        return;
      }

      if (restrictedMetaField === 'true') {
        if (restricted === 'disabled') {
          addToCartButton?.classList.remove('hidden');
          notifyMeButton?.classList.add('hidden');
        } else {
          addToCartButton?.classList.add('hidden');
          notifyMeButton?.classList.remove('hidden');
        }
        return;
      }

      if (addToCartButton?.disabled) {
        addToCartButton.classList.add('hidden');
        notifyMeButton?.classList.remove('hidden');
      } else {
        addToCartButton?.classList.remove('hidden');
        notifyMeButton?.classList.add('hidden');
      }
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.submitButton) return;
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      const accessoriesUpsells = this.closest('product-info')?.querySelectorAll('input[name="upsell_accesories"]:checked');
      const buttonSpan = this.submitButton.querySelector('span');
      const buttonSpanText = buttonSpan?.innerHTML;
      const id = this.form.querySelector('[name=id]').value;
      const items = [];
      let quantity = 1;

      if (document.querySelector('[data-cart-quantity]')) {
        quantity = parseInt(document.querySelector('[data-cart-quantity]').value, 10);
      }

      let properties = {};
      const giftCardForm = this.form.querySelector('recipient-form');

      if (giftCardForm) {
        const isGiftChecked = giftCardForm.querySelector('[name="properties[__shopify_send_gift_card_to_recipient]"]').checked;
        const giftRecipientEmail = giftCardForm.querySelector('[name="properties[Recipient email]"]').value;

        if (isGiftChecked && giftRecipientEmail !== '') {
          const giftCardFormProperties = giftCardForm.querySelectorAll('[name*="properties"]');
          giftCardFormProperties.forEach((property) => {
            if (!property.disabled && property.value !== '') {
              let propertyName = property.getAttribute('name').replace('properties[', '').replace(']', '');
              properties[propertyName] = property.value;
            }
          });
        }
      }

      if (accessoriesUpsells && accessoriesUpsells.length > 0) {
        accessoriesUpsells.forEach((upsell) => {
          items.push({
            id: upsell.value,
            quantity: 1,
          });
        });
      }

      items.push({
        id,
        quantity: quantity || 0,
        properties,
      });

      const genericCartError = 'Something went wrong. Please try again.';

      await window.theme.addToCart(items)
        .then((result) => {
          // Guard against a missing/undefined result: on a 409/429/504 the cart
          // request can resolve without a value, so reading result.error directly
          // would throw and freeze the button. Fail gracefully instead.
          const rawError = result?.error;
          const errorMessage = rawError
            ? (typeof rawError === 'string' ? rawError : (rawError.message || genericCartError))
            : null;

          if (!result || errorMessage) {
            this.error = true;
            this.handleErrorMessage(errorMessage || genericCartError);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span')?.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            soldOutMessage.textContent = errorMessage || genericCartError;
            return;
          }
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            quickAddModal.hide(true);
          }
        })
        .catch(() => {
          this.error = true;
          this.handleErrorMessage(genericCartError);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');

          if (!this.error && buttonSpan && !buttonSpan.closest('.product-card-wrapper')) {
            buttonSpan.innerHTML = 'Added!';
            setTimeout(function () {
              buttonSpan.innerHTML = buttonSpanText;
            }, 3000);
          }
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
